// src/components/compliance/modals/RecommendationHandoffPreviewModal.jsx
// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

import React from "react";
import ModalShell from "./shared/ModalShell";
import ModalSection from "./shared/ModalSection";

const CONNECTOR_PROVIDER_DISPLAY_NAMES = {
  aws: "AWS",
  cloudflare: "Cloudflare",
  github: "GitHub",
  google_workspace: "Google Workspace",
  okta: "Okta",
};

function cleanConnectorProvider(value) {
  return String(value || "").trim().toLowerCase();
}

function formatConnectorProvider(value) {
  const normalizedProvider = cleanConnectorProvider(value);

  if (CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider]) {
    return CONNECTOR_PROVIDER_DISPLAY_NAMES[normalizedProvider];
  }

  return String(value || "Unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function titleCaseLabel(value, fallback = "—") {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.filter((item) => item !== undefined && item !== null && item !== "");
  }
  return [value].filter((item) => item !== undefined && item !== null && item !== "");
}

function formatList(value, fallback = "—") {
  const items = normalizeList(value)
    .map((item) => String(item).trim())
    .filter(Boolean);

  return items.length ? items.join(", ") : fallback;
}

function formatStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "Preview only";
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatRecommendedWorkType(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "Recommendation handoff";
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatPriority(value) {
  const normalized = String(value || "medium").trim().toLowerCase();
  if (normalized === "critical") return "Critical";
  if (normalized === "high") return "High";
  if (normalized === "low") return "Low";
  return "Medium";
}

function priorityColor(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "critical" || normalized === "high") return "#fb7185";
  if (normalized === "medium") return "#fbbf24";
  return "#34d399";
}

const summaryBarStyle = {
  border: "1px solid rgba(59, 130, 246, 0.28)",
  borderRadius: 16,
  padding: "12px 14px",
  background: "rgba(30, 64, 175, 0.14)",
  color: "#bfdbfe",
  display: "grid",
  gap: 6,
};

const summaryTitleStyle = {
  color: "#f8fafc",
  fontSize: 14,
  fontWeight: 900,
};

const summaryHintStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.45,
};

const metricTileStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 14,
  padding: "10px 12px",
  background: "rgba(15, 23, 42, 0.36)",
  display: "grid",
  gap: 4,
};

const metricLabelStyle = {
  color: "#93c5fd",
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const metricValueStyle = {
  color: "#f8fafc",
  fontSize: 14,
  fontWeight: 800,
  lineHeight: 1.35,
};

const handoffTitleGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 10,
  marginBottom: 12,
};

const handoffTitleTileStyle = {
  border: "1px solid rgba(59, 130, 246, 0.24)",
  borderRadius: 16,
  padding: "12px 14px",
  background: "rgba(30, 64, 175, 0.12)",
  display: "grid",
  gap: 6,
  minWidth: 0,
};

const handoffTitleValueStyle = {
  color: "#f8fafc",
  fontSize: 15,
  fontWeight: 900,
  lineHeight: 1.35,
  overflowWrap: "anywhere",
  wordBreak: "normal",
};

const supportingMetricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
  gap: 10,
};

const bodyTextStyle = {
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.55,
  margin: 0,
};

const noticeStyle = {
  border: "1px solid rgba(52, 211, 153, 0.35)",
  borderRadius: 14,
  padding: "10px 12px",
  background: "rgba(6, 78, 59, 0.18)",
  color: "#bbf7d0",
  fontSize: 13,
  lineHeight: 1.45,
};

const draftCreatedStyle = {
  ...noticeStyle,
  display: "grid",
  gap: 6,
};

const errorStyle = {
  ...noticeStyle,
  border: "1px solid rgba(248, 113, 113, 0.38)",
  background: "rgba(127, 29, 29, 0.20)",
  color: "#fecaca",
};

const guardrailStyle = {
  ...noticeStyle,
  border: "1px solid rgba(251, 191, 36, 0.34)",
  background: "rgba(120, 53, 15, 0.16)",
  color: "#fde68a",
};

const codeBlockStyle = {
  border: "1px solid rgba(148, 163, 184, 0.18)",
  borderRadius: 14,
  padding: "10px 12px",
  background: "rgba(2, 6, 23, 0.42)",
  color: "#dbeafe",
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
};

const chipRowStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  alignItems: "center",
};

const chipStyle = {
  borderRadius: 999,
  padding: "4px 9px",
  fontSize: 11,
  fontWeight: 900,
  background: "rgba(59, 130, 246, 0.12)",
  border: "1px solid rgba(59, 130, 246, 0.24)",
  color: "#bfdbfe",
};

const stepListStyle = {
  margin: 0,
  paddingLeft: 18,
  color: "#bfdbfe",
  fontSize: 13,
  lineHeight: 1.55,
};

function MetricTile({ label, value }) {
  return (
    <div style={metricTileStyle}>
      <div style={metricLabelStyle}>{label}</div>
      <div style={metricValueStyle}>{value || "—"}</div>
    </div>
  );
}

function HandoffTitleTile({ label, value }) {
  return (
    <div style={handoffTitleTileStyle}>
      <div style={metricLabelStyle}>{label}</div>
      <div style={handoffTitleValueStyle}>{value || "—"}</div>
    </div>
  );
}

export default function RecommendationHandoffPreviewModal({
  open,
  preview = null,
  loading = false,
  error = "",
  creatingDraftHandoff = false,
  draftHandoffResult = null,
  draftHandoffError = "",
  onCreateDraftHandoff,
  onViewDraftHandoffs,
  onClose,
}) {
  const previewPayload = preview?.preview || preview || {};
  const executionStatus = preview?.execution_status || previewPayload.execution_status || "preview_only";
  const guardrails = normalizeList(previewPayload.guardrails);
  const suggestedSteps = normalizeList(previewPayload.suggested_steps);
  const controls = normalizeList(previewPayload.controls);
  const findings = normalizeList(previewPayload.findings);
  const resources = normalizeList(previewPayload.resources);
  const relatedTaskIds = normalizeList(previewPayload.related_task_ids);

  const title = previewPayload.suggested_workflow_title || "Recommendation handoff preview";
  const taskTitle = previewPayload.suggested_task_title || "Draft recommendation handoff";
  const workType = formatRecommendedWorkType(previewPayload.recommended_work_type);
  const priority = formatPriority(previewPayload.priority);
  const connector = previewPayload.connector
    ? formatConnectorProvider(previewPayload.connector)
    : "No connector";
  const category = titleCaseLabel(previewPayload.category || "recommendation");
  const draftHandoff = draftHandoffResult?.handoff || null;
  const draftExecutionStatus = String(draftHandoffResult?.execution_status || "").trim();
  const hasDraftHandoff = Boolean(draftHandoff?.id);
  const draftCreatedLabel =
    draftExecutionStatus === "draft_existing"
      ? "Draft handoff already exists"
      : "Draft handoff created";

  return (
    <ModalShell
      open={open}
      ariaLabel="Recommendation handoff preview"
      eyebrow="OASYS · Preview Only"
      title="Recommendation Handoff Preview"
      subtitle="Review the proposed workflow/task handoff before any operational work is created."
      onClose={onClose}
      maxWidth="960px"
      accentBorder="rgba(59, 130, 246, 0.52)"
      zIndex={175}
    >
      {loading ? (
        <div style={summaryBarStyle}>
          <div style={summaryTitleStyle}>Preparing handoff preview…</div>
          <div style={summaryHintStyle}>
            OASYS is building a preview-only handoff package. No workflow, remediation task, validation run, or evidence approval is being created.
          </div>
        </div>
      ) : error ? (
        <div style={errorStyle}>
          {String(error || "Unable to prepare handoff preview.")}
        </div>
      ) : previewPayload && Object.keys(previewPayload).length ? (
        <>
          <div style={summaryBarStyle}>
            <div style={chipRowStyle}>
              <span style={chipStyle}>{formatStatus(executionStatus)}</span>
              <span style={chipStyle}>{workType}</span>
              <span
                style={{
                  ...chipStyle,
                  color: priorityColor(previewPayload.priority),
                  borderColor: priorityColor(previewPayload.priority),
                  background: "rgba(15, 23, 42, 0.36)",
                }}
              >
                {priority}
              </span>
              <span style={chipStyle}>{category}</span>
              <span style={chipStyle}>{connector}</span>
            </div>
            <div style={summaryTitleStyle}>{title}</div>
            <div style={summaryHintStyle}>
              {previewPayload.handoff_summary ||
                "This is a preview-only handoff. OASYS has not created or executed operational work."}
            </div>
          </div>

          <ModalSection
            title="Suggested work package"
            description="OASYS has prepared a draft handoff shape only. The next product step can convert this into a draft workflow or remediation task after an operator confirms."
            badge="Preview"
            badgeTone="medium"
          >
            <div>
              <div style={handoffTitleGridStyle}>
                <HandoffTitleTile
                  label="Suggested workflow"
                  value={previewPayload.suggested_workflow_title}
                />
                <HandoffTitleTile label="Suggested task" value={taskTitle} />
              </div>

              <div style={supportingMetricGridStyle}>
                <MetricTile label="Framework" value={previewPayload.framework || "—"} />
                <MetricTile label="Connector" value={connector} />
                <MetricTile label="Controls" value={formatList(controls)} />
                <MetricTile label="Findings" value={formatList(findings)} />
                <MetricTile label="Resources" value={formatList(resources)} />
                <MetricTile label="Related tasks" value={formatList(relatedTaskIds)} />
              </div>
            </div>
          </ModalSection>

          <ModalSection
            title="Task description preview"
            description="This text is suitable for a future draft task or workflow handoff. It is not saved as work yet."
            badge="Draft text"
            badgeTone="default"
          >
            <div style={codeBlockStyle}>
              {previewPayload.suggested_task_description ||
                "No task description was returned for this handoff preview."}
            </div>
          </ModalSection>

          <ModalSection
            title="Suggested next steps"
            description="These steps describe what an operator would review after a draft handoff is created."
            badge={`${suggestedSteps.length} step${suggestedSteps.length === 1 ? "" : "s"}`}
            badgeTone="default"
          >
            {suggestedSteps.length ? (
              <ol style={stepListStyle}>
                {suggestedSteps.map((step, index) => (
                  <li key={`${step}-${index}`}>{String(step)}</li>
                ))}
              </ol>
            ) : (
              <p style={bodyTextStyle}>No suggested steps were returned.</p>
            )}
          </ModalSection>

          <ModalSection
            title="Execution guardrails"
            description="This checkpoint intentionally stops before creating work. Creation belongs to a later explicit operator-confirmed step."
            badge="No execution"
            badgeTone="low"
          >
            {guardrails.length ? (
              <div style={{ display: "grid", gap: 8 }}>
                {guardrails.map((guardrail, index) => (
                  <div key={`${guardrail}-${index}`} style={guardrailStyle}>
                    {String(guardrail)}
                  </div>
                ))}
              </div>
            ) : (
              <div style={guardrailStyle}>
                Preview only. No remediation, workflow, task, evidence approval, or validation run has been executed.
              </div>
            )}
          </ModalSection>

          {hasDraftHandoff ? (
            <div style={draftCreatedStyle}>
              <strong style={{ color: "#f8fafc" }}>{draftCreatedLabel}</strong>
              <span>
                Draft handoff ID: {draftHandoff.id}. This is a planning record only. OASYS has not executed remediation, created a remediation task, approved evidence, advanced workflow, or run validation.
              </span>
            </div>
          ) : null}

          {draftHandoffError ? (
            <div style={errorStyle}>{String(draftHandoffError)}</div>
          ) : null}

          {hasDraftHandoff ? (
            <div style={noticeStyle}>
              Next available action: View draft handoffs. The draft is saved as a planning record only.
            </div>
          ) : (
            <div style={noticeStyle}>
              Next available action: {titleCaseLabel(previewPayload.next_available_action || "create_draft_handoff")}. Use Create draft handoff to save this as a planning record only.
            </div>
          )}
        </>
      ) : (
        <p style={bodyTextStyle}>No handoff preview is loaded.</p>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          flexWrap: "wrap",
          marginTop: 16,
        }}
      >
        <button
          type="button"
          className={hasDraftHandoff ? "btn btn-ghost btn-sm" : "btn btn-primary btn-sm"}
          disabled={
            loading ||
            creatingDraftHandoff ||
            hasDraftHandoff ||
            typeof onCreateDraftHandoff !== "function" ||
            !previewPayload?.recommendation_id
          }
          onClick={onCreateDraftHandoff}
        >
          {hasDraftHandoff
            ? "Draft created"
            : creatingDraftHandoff
            ? "Creating draft…"
            : "Create draft handoff"}
        </button>

        {hasDraftHandoff ? (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={typeof onViewDraftHandoffs !== "function"}
            onClick={onViewDraftHandoffs}
          >
            View draft handoffs
          </button>
        ) : null}

        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </ModalShell>
  );
}